import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Tooltip,
  useToast,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
} from "@chakra-ui/react";

import {
  HiOutlineArrowNarrowLeft,
  HiOutlineSave,
  HiOutlinePlus,
  HiOutlinePencil,
} from "react-icons/hi";

import CodesetSelect from "./CodesetSelect";
import PlacingStepper from "./PlacingStepper";
import CodexLookupText from "./CodexLookupText";
import { getSignalPayload } from "./utils/getSignalPayload";
import DocumentDefinitionSelectById from "./DocumentDefinitionSelectById";

const jsonEqual = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

const getIdish = (val) => {
  if (!val) return null;
  if (typeof val === "string") return val;
  return val._id ?? val.id ?? val.value ?? null;
};

const ContractInstanceEditor = ({
  productCode,
  contractInstance,
  productInstance,
  onSave,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const DOCS_API = "https://documents.poc.mrc.ai/api/documents";
  const [docRepoCache, setDocRepoCache] = useState({}); // { [repoId]: { name, createdDate } }
  const [creatingDocIndex, setCreatingDocIndex] = useState(null);

  const [form, setForm] = useState(contractInstance || {});
  const [isDirty, setIsDirty] = useState(false);
  const [serverSnapshot, setServerSnapshot] = useState(contractInstance || {});
  const [hasRemoteUpdate, setHasRemoteUpdate] = useState(false);

  const [registers, setRegisters] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(true);

  // track “active editing” to pause polling while user is typing/focused
  const editingCountRef = useRef(0);
  const isEditing = () => editingCountRef.current > 0;

const openDocumentEditor = (doc) => {
  // Require a linked repo so the editor can load from Documents API

  console.log("Opening document editor for", doc);
  if (!doc?.documentRepositoryId) {
    toast({
      title: "No linked document",
      description: "Create & link the document first, then edit.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
    return;
  }
  console.log("Navigating to document editor for repo id", doc.id);
  // Navigate to the loader; it will pick the doc via ?doc=<documentId>
  navigate(`/editor/${productCode}/${form._id}?doc=${doc.id}`);
};

  // === Keep local form in sync when parent sends a new instance ===
  useEffect(() => {
    setForm(contractInstance || {});
    setServerSnapshot(contractInstance || {});
    setIsDirty(false);
    setHasRemoteUpdate(false);
  }, [contractInstance]);

  // === Fetch registers ===
  useEffect(() => {
    const fetchRegisters = async () => {
      try {
        const res = await fetch("https://registers.poc.mrc.ai/api/registers");
        if (!res.ok) throw new Error("Failed to fetch registers");
        const data = await res.json();
        setRegisters(data);
      } catch (err) {
        console.error("❌ Error fetching registers", err);
      } finally {
        setRegisterLoading(false);
      }
    };
    fetchRegisters();
  }, []);

  // === Global focus listeners to pause polling ===
  useEffect(() => {
    const onFocusIn = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (
        ["input", "textarea", "select"].includes(tag) ||
        e.target?.isContentEditable
      ) {
        editingCountRef.current += 1;
      }
    };
    const onFocusOut = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (
        ["input", "textarea", "select"].includes(tag) ||
        e.target?.isContentEditable
      ) {
        editingCountRef.current = Math.max(0, editingCountRef.current - 1);
      }
    };
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    return () => {
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  // === Warn on unload if dirty ===
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = ""; // required for Chrome
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // === Helpers ===
  const markDirty = (updater) => {
    setForm((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!jsonEqual(next, prev)) setIsDirty(true);
      return next;
    });
  };

  const handleChange = (key, value) => {
    markDirty((prev) => ({ ...prev, [key]: value }));
  };

  // ----- Document helpers -----
  // Merge-friendly: can accept an id string, a doc object, or a { definitionId } shape.
  const handleDocumentChange = (docIndex, newVal) => {
    markDirty((prev) => {
      const docs = Array.isArray(prev.documents) ? [...prev.documents] : [];
      if (docIndex < 0 || docIndex >= docs.length) return prev;

      const current = docs[docIndex] || {};
      let patch = {};

      // If it's a string or selection object, treat as a definition change
      const tryId = getIdish(newVal);
      if (tryId) {
        patch.definitionId = tryId;
        patch.selectedDefinitionId = tryId;
      }

      // If caller passed a doc-like object with fields (e.g., { status: 'draft' })
      if (newVal && typeof newVal === "object" && !tryId) {
        patch = { ...patch, ...newVal };
      }

      docs[docIndex] = { ...current, ...patch };
      return { ...prev, documents: docs };
    });
  };

  const handleDocumentFieldChange = (docIndex, field, value) => {
    markDirty((prev) => {
      const docs = Array.isArray(prev.documents) ? [...prev.documents] : [];
      if (docIndex < 0 || docIndex >= docs.length) return prev;
      const current = docs[docIndex] || {};
      docs[docIndex] = { ...current, [field]: value };
      return { ...prev, documents: docs };
    });
  };

  const handleAddDocument = () => {
    markDirty((prev) => {
      const docs = Array.isArray(prev.documents) ? prev.documents : [];
      const newDocument = {
        id:
          (typeof crypto !== "undefined" &&
            crypto?.randomUUID &&
            crypto.randomUUID()) ||
          `${Date.now()}-${Math.random()}`,
        status: "",
        definitionId: null,
        selectedDefinitionId: null,
      };
      return { ...prev, documents: [...docs, newDocument] };
    });
  };

  const handleRemoveDocument = (docIndex) => {
    markDirty((prev) => {
      const docs = Array.isArray(prev.documents) ? prev.documents : [];
      if (docIndex < 0 || docIndex >= docs.length) return prev;
      const nextDocs = docs.filter((_, i) => i !== docIndex);
      return { ...prev, documents: nextDocs };
    });
  };
  // ----------------------------

  // === Poll the contract for live updates (but be polite) ===
  useEffect(() => {
    if (!contractInstance?._id || !productCode) return;

    const tick = async () => {
      // Pause polling if user is actively editing or tab hidden
      if (document.visibilityState === "hidden" || isEditing()) return;

      try {
        const res = await fetch(
          `https://poc.mrc.ai/${productCode}/api/contracts/${contractInstance._id}`
        );
        if (!res.ok) throw new Error("Failed to refresh contract data");
        const updated = await res.json();

        if (isDirty) {
          // Don't overwrite local edits — just store a fresh snapshot and notify
          setServerSnapshot(updated);
          setHasRemoteUpdate(true);
          return;
        }

        // Only adopt if different
        if (!jsonEqual(updated, form)) {
          setForm(updated);
          setServerSnapshot(updated);
          setHasRemoteUpdate(false);
        }
      } catch (err) {
        console.warn("Polling failed:", err.message);
      }
    };

    const interval = setInterval(tick, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractInstance?._id, productCode, isDirty, form]);

  useEffect(() => {
    const docs = Array.isArray(form?.documents) ? form.documents : [];
    docs
      .map((d) => d?.documentRepositoryId)
      .filter((id) => !!id && !docRepoCache[id])
      .forEach((id) => hydrateRepoDocName(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.documents]);

  const handleSubmit = async () => {
    try {
      const res = await fetch(
        `https://poc.mrc.ai/${productCode}/api/contracts/${contractInstance._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            // "If-Match": form._etag ?? undefined, // if your API supports optimistic concurrency
          },
          body: JSON.stringify(form),
        }
      );
      console.log("Save response:", res);
      console.log("Saved payload:", JSON.stringify(form, null, 2));
      if (!res.ok) throw new Error("Failed to update contract");

      const saved = (await res.json().catch(() => null)) || form;

      setServerSnapshot(saved);
      setForm(saved);
      setIsDirty(false);
      setHasRemoteUpdate(false);

      toast({ title: "Contract saved", status: "success" });
      onSave?.(saved);
    } catch (err) {
      console.error("❌ Error saving contract", err);
      toast({ title: "Error saving contract", status: "error" });
    }
  };

  const handleSignal = (signal, control) => {
    switch (signal) {
      case "contractBound":
        getSignalPayload(control.registerType, control.entryType)
          .then((payload) => {
            if (!payload) {
              toast({
                title: "No signal payload",
                description: "Please load a signal first.",
                status: "warning",
              });
              return;
            }
            payload.type = "contractBound";
            payload.data[0].references = [
              { type: "contract_id", value: contractInstance._id },
            ];

            console.log("Sending signal payload:", JSON.stringify(payload, null, 2));

            fetch(
              `https://poc.mrc.ai/${productCode}/api/signals/standard-signal`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload, null, 2),
              }
            )
              .then(async (res) => {
                if (!res.ok) throw new Error("Failed to send signal");
                return res.headers
                  .get("content-type")
                  ?.includes("application/json")
                  ? res.json()
                  : {};
              })
              .then(() => {
                toast({ title: "Signal sent successfully", status: "success" });
              })
              .catch((err) => {
                console.error("❌ Error sending signal", err);
                toast({
                  title: "Error sending signal",
                  description: err.message,
                  status: "error",
                });
              });
          })
          .catch((err) => {
            toast({
              title: "Error loading signal",
              description: err.message,
              status: "error",
            });
          });
        break;

      default:
        toast({
          title: "Unknown signal",
          description: `Unhandled signal type: ${signal}`,
          status: "warning",
        });
    }
  };

  // GET a document by id (best effort) and cache its name
  const hydrateRepoDocName = async (repoId) => {
    if (!repoId || docRepoCache[repoId]) return;
    try {
      const res = await fetch(`${DOCS_API}/${repoId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) throw new Error("Failed to read document");
      const doc = await res.json();
      setDocRepoCache((m) => ({
        ...m,
        [repoId]: { name: doc?.name ?? repoId, createdDate: doc?.createdDate },
      }));
    } catch {
      // fallback to id as name if GET is not available
      setDocRepoCache((m) => ({ ...m, [repoId]: { name: repoId } }));
    }
  };

  // POST create + link a document for this row, using its definitionId
  const createAndLinkDocument = async (docIndex) => {
    const docs = Array.isArray(form?.documents) ? form.documents : [];
    const row = docs[docIndex];
    const definitionId = row?.definitionId ?? row?.selectedDefinitionId;

    if (!definitionId) {
      toast({ title: "Pick a document type first", status: "warning" });
      return;
    }

    try {
      setCreatingDocIndex(docIndex);

      const res = await fetch(DOCS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ definitionId, name: "New Document" }),
      });
      if (!res.ok) throw new Error("Failed to create document");

      const created = await res.json(); // { _id, definitionId, name, content, createdDate }

      // 1) cache the name so we can display immediately
      setDocRepoCache((m) => ({
        ...m,
        [created._id]: { name: created.name, createdDate: created.createdDate },
      }));

      // 2) write documentRepositoryId back into the row
      handleDocumentFieldChange(docIndex, "documentRepositoryId", created._id);

      toast({
        title: "Document created & linked",
        description: created.name,
        status: "success",
      });
    } catch (err) {
      console.error("❌ Create document error", err);
      toast({
        title: "Error creating document",
        description: err.message,
        status: "error",
      });
    } finally {
      setCreatingDocIndex(null);
    }
  };

  return (
    <Box p={6} maxWidth="100%" mx="auto">
      <HStack>
        <Tooltip
          label="Back to the Product Dashboard"
          aria-label="Product Dashboard tooltip">
          <IconButton
            icon={<HiOutlineArrowNarrowLeft />}
            size="md"
            onClick={() => window.history.back()}
          />
        </Tooltip>
        <Heading size="lg">{form?.name || contractInstance?.name}</Heading>
        <Tooltip
          label="Save Contract Instance"
          aria-label="Save Contract Instance tooltip">
          <IconButton
            icon={<HiOutlineSave />}
            size="md"
            onClick={handleSubmit}
          />
        </Tooltip>
      </HStack>

      <Box
        borderWidth="1px"
        borderRadius="md"
        p={4}
        borderColor="gray.200"
        backgroundColor="whiteAlpha.800"
        mt={4}>
        <PlacingStepper
          showSubSteps={false}
          size="lg"
          stageCode={form?.placingStageCode}
          transactionCode={form?.placingTransactionFunctionCode}
          defaultOrientation="horizontal"
        />
      </Box>

      {/* Controls */}
      <Box
        borderWidth="1px"
        borderRadius="md"
        mt={4}
        borderColor="gray.200"
        backgroundColor="whiteAlpha.800">
        <Table variant="simple" size="sm">
          <Tbody>
            {Array.isArray(productInstance?.controls) &&
            productInstance.controls.length > 0 ? (
              productInstance.controls.map((control, index) => {
                const register = registers.find(
                  (r) => r._id === control.registerType
                );
                const entry = register?.entries?.find(
                  (e) => e.id === control.entryType
                );
                return (
                  <Tr key={index}>
                    <Td whiteSpace="nowrap" w="1%">
                      <HStack spacing={1}>
                        <Button
                          size="sm"
                          colorScheme="linkedin"
                          onClick={() => handleSignal(control.signal, control)}>
                          {control.name || entry?.name || "Unnamed Control"}
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td>No contract controls defined.</Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      <Box
        borderWidth="1px"
        borderRadius="md"
        p={4}
        borderColor="gray.500"
        backgroundColor="whiteAlpha.800"
        mt={4}>
        <Tabs variant="line" colorScheme="linkedin">
          <TabList>
            <Tab>Header</Tab>
            <Tab>Documents</Tab>
            <Tab>Semaphores</Tab>
            <Tab>Roles</Tab>
            <Tab>Activities</Tab>
            <Tab>Logs</Tab>
          </TabList>
          <TabPanels>
            {/* Header */}
            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={4}>
                <Box>
                  <Stack spacing={4}>
                    <FormControl>
                      <FormLabel>Id</FormLabel>
                      <FormLabel>
                        {form?._id || contractInstance?._id || ""}
                      </FormLabel>
                    </FormControl>

                    <FormControl>
                      <FormLabel>Contract Name</FormLabel>
                      <Input
                        value={form?.name || ""}
                        onChange={(e) => handleChange("name", e.target.value)}
                      />
                    </FormControl>

                    <CodesetSelect
                      label="Type"
                      codesetCode="MRCAI001"
                      value={
                        form?.type ? { code: form.type, description: "" } : null
                      }
                      onChange={(value) =>
                        handleChange("type", value?.code ?? "")
                      }
                    />

                    <CodesetSelect
                      label="Placing Stage"
                      codesetCode="A83"
                      filter="#Placing"
                      value={
                        form?.placingStageCode
                          ? { code: form.placingStageCode, description: "" }
                          : null
                      }
                      onChange={(value) =>
                        handleChange("placingStageCode", value?.code ?? "")
                      }
                    />

                    <CodesetSelect
                      label="Placing Transaction"
                      codesetCode="1225"
                      filter="#Placing"
                      value={
                        form?.placingTransactionFunctionCode
                          ? {
                              code: form.placingTransactionFunctionCode,
                              description: "",
                            }
                          : null
                      }
                      onChange={(value) =>
                        handleChange(
                          "placingTransactionFunctionCode",
                          value?.code ?? ""
                        )
                      }
                    />
                  </Stack>
                </Box>
              </SimpleGrid>
            </TabPanel>

            {/* Documents */}
            <TabPanel>
              <HStack mb={2}>
                <Tooltip label="Add Document" aria-label="Add Document Tooltip">
                  <IconButton
                    icon={<HiOutlinePlus />}
                    size="md"
                    onClick={handleAddDocument}
                  />
                </Tooltip>
              </HStack>

              {Array.isArray(form?.documents) && form.documents.length > 0 ? (
                <Box overflowX="auto" mt={2}>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th whiteSpace="nowrap" w="1%">
                          Status
                        </Th>
                        <Th whiteSpace="nowrap" w="1%">
                          Type
                        </Th>
                        <Th whiteSpace="nowrap" w="1%">
                          Create
                        </Th>
                        <Th whiteSpace="nowrap" w="1%">
                          Linked Document
                        </Th>
                        <Th whiteSpace="nowrap" w="1%">
                          Doc UUID
                        </Th>
                        <Th whiteSpace="nowrap" w="1%">
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {form.documents.map((doc, docIndex) => {
                        const docId = doc?.id;
                        const repoId = doc?.documentRepositoryId;
                        const repoName = repoId
                          ? docRepoCache[repoId]?.name
                          : "";

                        return (
                          <Tr key={doc.id ?? docIndex}>
                            <Td whiteSpace="nowrap" w="1%">
                              <CodesetSelect
                                label=""
                                codesetCode="MRCAI010"
                                value={
                                  doc.status
                                    ? { code: doc.status, description: "" }
                                    : null
                                }
                                onChange={(value) =>
                                  handleDocumentFieldChange(
                                    docIndex,
                                    "status",
                                    value?.code ?? ""
                                  )
                                }
                              />
                            </Td>

                            <Td whiteSpace="nowrap" w="1%">
                              <DocumentDefinitionSelectById
                                label=""
                                selectedDocumentDefinitionId={
                                  doc.definitionId ??
                                  doc.selectedDefinitionId ??
                                  null
                                }
                                onChange={(newVal) =>
                                  handleDocumentChange(docIndex, newVal)
                                }
                              />
                            </Td>
                            <Td whiteSpace="nowrap" w="1%">
                              <Tooltip
                                label="Create Document"
                                aria-label="Create Document Tooltip">
                                <IconButton
                                  icon={<HiOutlinePlus />}
                                  size="sm"
                                  isLoading={creatingDocIndex === docIndex}
                                  onClick={() =>
                                    createAndLinkDocument(docIndex)
                                  }
                                  isDisabled={!!repoId} // prevent duplicates once linked
                                />
                              </Tooltip>
                            </Td>
                            <Td whiteSpace="nowrap" w="1%">
                              {repoId ? (
                                <Text title={repoId}>
                                  {repoName || "Loading…"}
                                </Text>
                              ) : (
                                <Text color="gray.500" fontStyle="italic">
                                  Not linked
                                </Text>
                              )}
                            </Td>
                            <Td>
                              <FormControl>
                                <Input
                                  fontSize="xs"
                                  value={doc.documentRepositoryId || ""}
                                />
                              </FormControl>
                            </Td>
<Td whiteSpace="nowrap" w="1%">
  <Tooltip label="Edit Document" aria-label="Edit Document Tooltip">
    <IconButton
      icon={<HiOutlinePencil />}
      size="sm"
      onClick={() => openDocumentEditor(doc)}
      isDisabled={!doc?.documentRepositoryId} // optional safety
    />
  </Tooltip>
</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500" fontStyle="italic">
                  No documents added for this contract.
                </Text>
              )}
            </TabPanel>

            {/* Semaphores */}
            <TabPanel>
              {Array.isArray(form?.semaphores) && form.semaphores.length > 0 ? (
                <Box overflowX="auto" mt={4}>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th whiteSpace="nowrap" w="1%">
                          Type
                        </Th>
                        <Th whiteSpace="nowrap" w="1%">
                          Value
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {form.semaphores.map((sem, index) => (
                        <Tr key={index}>
                          <Td whiteSpace="nowrap" w="1%">
                            <CodexLookupText
                              label=""
                              version="weirddata_v1"
                              codelist="MRCAI002"
                              codeValue={sem.type}
                            />
                          </Td>
                          <Td whiteSpace="nowrap" w="1%">
                            <CodexLookupText
                              label=""
                              version="weirddata_v1"
                              codelist="JVRI018"
                              codeValue={sem.value}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500" fontStyle="italic">
                  No semaphores defined for this contract.
                </Text>
              )}
            </TabPanel>

            <TabPanel></TabPanel>
            <TabPanel></TabPanel>
            <TabPanel></TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {hasRemoteUpdate && isDirty && (
        <Box
          mt={3}
          p={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor="yellow.300"
          bg="yellow.50">
          <HStack justify="space-between">
            <Text fontSize="sm">
              Newer version available on the server. Save your changes or
              discard to refresh.
            </Text>
            <HStack>
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  setForm(serverSnapshot);
                  setIsDirty(false);
                  setHasRemoteUpdate(false);
                }}>
                Discard & Refresh
              </Button>
              <Button size="xs" colorScheme="linkedin" onClick={handleSubmit}>
                Save Now
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default ContractInstanceEditor;
